import React, { useState, useEffect } from 'react';
import { 
  Theater,
  Upload, 
  Download,
  AlertCircle, 
  Loader2, 
  CalendarDays,
  Calendar,
  MapPin,
  CheckCircle,
  FileSpreadsheet,
  Trash2,
  Clock,
  Users,
  Settings,
  Plus,
  Edit,
  Hash,
  ChevronRight,
  Search,
  X,
  ArrowUpDown,
  ChevronLeft,
  Check,
  AlertTriangle,
  FileText,
  MoreVertical
} from 'lucide-react';
import { fetchWithAuth, SEASON_EVENTS_API, api } from '../../utils/api';
import { useTheme } from '../../context/ThemeContext';
import StageManagementModal from '../../components/admin/StageManagementModal';
import EnhancedRoutineViewer from '../../components/admin/EnhancedRoutineViewer';
import ScheduleCreator from '../../components/admin/ScheduleCreator';

/**
 * StagesManager
 *
 * Clean table-based event management with redesigned modal workflow
 */
const StagesManager = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState(null);
  
  // Event stage configuration state
  const [eventStages, setEventStages] = useState({}); // { eventName: { stageCount: 2, stages: [{ id: 1, name: "Main Stage", schedule: null }, ...] } }
  
  // Modal states
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentStep, setCurrentStep] = useState(0); // Step-based flow
  const [selectedStage, setSelectedStage] = useState(null);
  
  // Schedule Creator states
  const [showScheduleCreator, setShowScheduleCreator] = useState(false);
  const [scheduleCreatorData, setScheduleCreatorData] = useState({
    routines: [],
    stages: [],
    eventName: ''
  });
  
  // JSON Upload states
  const [showJSONUpload, setShowJSONUpload] = useState(false);
  const [jsonFile, setJsonFile] = useState(null);
  const [jsonUploading, setJsonUploading] = useState(false);
  const [jsonEventName, setJsonEventName] = useState('');
  
  // Stage configuration
  const [stageCount, setStageCount] = useState(1);
  const [stageNames, setStageNames] = useState(['Main Stage']);
  
  // CSV Upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvError, setCsvError] = useState(null);
  const [parsedRoutines, setParsedRoutines] = useState([]);
  const [uploadingStageId, setUploadingStageId] = useState(null);
  const [showRoutinePreview, setShowRoutinePreview] = useState(false);
  const [showEnhancedViewer, setShowEnhancedViewer] = useState(false);
  const [enhancedRoutineData, setEnhancedRoutineData] = useState([]);
  const [modalNotification, setModalNotification] = useState(null);
  const [routineSearchQuery, setRoutineSearchQuery] = useState('');

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  // Fetch events and stage configurations on component mount
  useEffect(() => {
    fetchEvents();
    fetchStageConfigurations();
  }, []);

  // Fetch Events from Season Events API
  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      
      const response = await fetchWithAuth(SEASON_EVENTS_API);
      
      if (!response) {
        throw new Error("No data received from API");
      }

      setSeasons(response.seasons || []);
      setEvents(response.events || []);
      
      // If there are seasons, set the first one as selected by default
      if (response.seasons && response.seasons.length > 0) {
        setSelectedSeason(response.seasons[0].name);
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      setNotification({ 
        type: 'error', 
        message: err.message || "Failed to fetch events. Please try again." 
      });
      setEvents([]);
      setSeasons([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Stage Configurations from Stages API
  const fetchStageConfigurations = async () => {
    try {
      const response = await api.getStageConfigurations();
      
      if (response && response.configurations) {
        // Convert configurations array to eventStages object format
        // Prioritize enhanced configurations and newer ones
        const stagesMap = {};
        
        // Sort configurations to prioritize enhanced and newer ones
        const sortedConfigurations = response.configurations.sort((a, b) => {
          // First priority: enhanced configurations
          if (a.enhanced && !b.enhanced) return -1;
          if (!a.enhanced && b.enhanced) return 1;
          
          // Second priority: newer configurations
          const aDate = new Date(a.updatedAt || a.createdAt || 0);
          const bDate = new Date(b.updatedAt || b.createdAt || 0);
          return bDate - aDate; // Newer first
        });
        
        sortedConfigurations.forEach(config => {
          // Only overwrite if we don't have a config for this event, 
          // or if the new config is enhanced and the existing one isn't
          const existingConfig = stagesMap[config.eventName];
          if (!existingConfig || (config.enhanced && !existingConfig.enhanced)) {
            stagesMap[config.eventName] = {
              stageCount: config.stageCount,
              stages: config.stages,
              enhanced: config.enhanced,
              source: config.source
            };
          }
        });
        setEventStages(stagesMap);
      }
    } catch (err) {
      console.error("Error fetching stage configurations:", err);
      // Don't show error notification for this as it's not critical
    }
  };

  // Filter and sort events (including synthetic events from stage configurations)
  const getFilteredAndSortedEvents = () => {
    // Start with events from Season Events API
    let allEvents = [...events];
    
    // Add synthetic events from enhanced stage configurations that don't have corresponding events
    Object.keys(eventStages).forEach(eventName => {
      const stageConfig = eventStages[eventName];
      // Only add if it's enhanced and doesn't already exist in events
      if (stageConfig.enhanced && !events.find(e => e.name === eventName)) {
        allEvents.push({
          name: eventName,
          city: 'Unknown', // We don't have this info from stage config
          season: '2025-2026', // Default season
          startDate: new Date().toISOString().split('T')[0], // Default to current date
          endDate: new Date().toISOString().split('T')[0],
          synthetic: true, // Mark as synthetic
          source: stageConfig.source || 'enhanced'
        });
      }
    });
    
    // Apply season filter
    let filtered = selectedSeason === 'all' 
      ? allEvents 
      : allEvents.filter(event => event.season === selectedSeason || event.synthetic);

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event => 
        event.name.toLowerCase().includes(query) ||
        (event.city && event.city.toLowerCase().includes(query)) ||
        (event.season && event.season.toLowerCase().includes(query)) ||
        (event.eventType && event.eventType.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        switch (sortConfig.key) {
          case 'name':
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case 'city':
            aValue = (a.city || '').toLowerCase();
            bValue = (b.city || '').toLowerCase();
            break;
          case 'startDate':
            aValue = parseDate(a.startDate);
            bValue = parseDate(b.startDate);
            break;
          case 'season':
            aValue = (a.season || '').toLowerCase();
            bValue = (b.season || '').toLowerCase();
            break;
          case 'stages':
            aValue = eventStages[a.name] ? eventStages[a.name].stageCount : 0;
            bValue = eventStages[b.name] ? eventStages[b.name].stageCount : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Parse date string to Date object, handling MM-DD-YYYY format
  const parseDate = (dateString) => {
    if (!dateString) return new Date(0); // Return epoch for null/empty dates
    
    // Handle MM-DD-YYYY format properly
    if (dateString.includes('-') && dateString.length === 10 && dateString.indexOf('-') === 2) {
      // MM-DD-YYYY format - parse directly to avoid timezone issues
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        // Use Date constructor with individual components to avoid timezone issues
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    }
    
    // For YYYY-MM-DD format, also parse components individually
    if (dateString.includes('-') && dateString.length === 10) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    }
    
    return new Date(dateString);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    const date = parseDate(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    
    // Format as MM/DD/YY
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2); // Get last 2 digits
    
    return `${month}/${day}/${year}`;
  };

  // Handle event selection
  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setModalNotification(null); // Clear any previous notifications
    
    // Check if this event already has stages configured
    if (eventStages[event.name]) {
      setCurrentStep(1); // Go to stage overview
    } else {
      setCurrentStep(0); // Start with configuration
      setStageCount(1);
      setStageNames(['Main Stage']);
    }
    
    setShowStageModal(true);
  };

  // Handle opening Schedule Creator
  const handleOpenScheduleCreator = async (event) => {
    try {
      const eventStageConfig = eventStages[event.name];
      if (!eventStageConfig) return;

      // Collect all routines from all stages for this event
      const allRoutines = [];
      const stageList = [];

      for (const stage of eventStageConfig.stages) {
        if (stage.schedule && Array.isArray(stage.schedule)) {
          allRoutines.push(...stage.schedule);
        }
        stageList.push({
          id: stage.id.toString(),
          name: stage.name
        });
      }

      setScheduleCreatorData({
        routines: allRoutines,
        stages: stageList,
        eventName: event.name
      });

      setShowScheduleCreator(true);
    } catch (error) {
      console.error('Error opening schedule creator:', error);
      setNotification({
        type: 'error',
        message: 'Failed to open schedule creator'
      });
    }
  };

  // Handle stage configuration
  const handleConfigureStages = async () => {
    if (!selectedEvent) return;
    
    try {
      // Create stages based on configuration
      const stages = stageNames.slice(0, stageCount).map((name, index) => ({
        id: index + 1,
        name: name.trim() || `Stage ${index + 1}`,
        schedule: null,
        routineCount: 0,
        uploadDate: null
      }));

      const stageConfig = {
        eventName: selectedEvent.name,
        stageCount,
        stages
      };

      // Save to backend
      await api.createStageConfiguration(stageConfig);

      // Update local state
      setEventStages(prev => ({
        ...prev,
        [selectedEvent.name]: {
          stageCount,
          stages
        }
      }));

      setCurrentStep(1); // Move to overview
      setModalNotification({
        type: 'success',
        message: `Successfully configured ${stageCount} stage(s) for ${selectedEvent.name}`
      });
    } catch (err) {
      console.error("Error configuring stages:", err);
      setModalNotification({
        type: 'error',
        message: `Failed to configure stages: ${err.message || 'Unknown error'}`
      });
    }
  };

  // Handle stage count change
  const handleStageCountChange = (count) => {
    setStageCount(count);
    const newStageNames = [...stageNames];
    
    // Add default names for new stages
    while (newStageNames.length < count) {
      newStageNames.push(`Stage ${newStageNames.length + 1}`);
    }
    
    setStageNames(newStageNames);
  };

  // Handle stage name change
  const handleStageNameChange = (index, name) => {
    const newStageNames = [...stageNames];
    newStageNames[index] = name;
    setStageNames(newStageNames);
  };



  // Handle CSV file selection with validation and preview
  const handleFileSelect = (event, stage) => {
    console.log('handleFileSelect called with:', { event, stage });
    const file = event.target.files[0];
    console.log('Selected file:', file);
    
    // Show immediate feedback in modal
    setModalNotification({ 
      type: 'success', 
      message: `Processing file: ${file ? file.name : 'No file selected'}...` 
    });
    
    if (file) {
      // Check file type more broadly for CSV files
      const isCSV = file.type === 'text/csv' || 
                   file.type === 'application/vnd.ms-excel' || 
                   file.name.toLowerCase().endsWith('.csv');
      
      if (isCSV) {
        console.log('File is valid CSV, proceeding with upload');
        setCsvFile(file);
        setCsvError(null);
        setSelectedStage(stage);
      
      // Parse and validate CSV
      const reader = new FileReader();
      reader.onload = async (readerEvent) => {
        try {
          const csvData = readerEvent.target.result;
          const rows = csvData.split('\n').filter(row => row.trim());
          
          if (rows.length < 2) {
            setCsvError('CSV file must contain at least a header row and one data row');
            return;
          }

          // Validate CSV structure
          console.log('Validating CSV structure...');
          const validationResult = validateCSVStructure(rows);
          if (!validationResult.isValid) {
            console.log('CSV structure validation failed:', validationResult.error);
            setCsvError(validationResult.error);
            setModalNotification({ 
              type: 'error', 
              message: validationResult.error 
            });
            return;
          }
          console.log('CSV structure validation passed');

          // Parse header row to find column indices
          const headers = rows[0].split(',').map(h => h.trim().toLowerCase());
          
          // Define required columns for our template
          const requiredColumns = {
            routineNumber: ['routine #', 'routine number', 'routine_number'],
            routineName: ['routine name', 'routine_name', 'name'],
            performerName: ['performer name', 'dancer name', 'performer_name', 'dancer_name'],
            studioCode: ['studio code', 'studio_code', 'studio'],
            category: ['category'],
            division: ['division', 'performance division'],
            type: ['type', 'style'],
            ageGroup: ['age', 'ages', 'age group', 'age_group'],
            dancersCount: ['# of dancers', 'dancers count', 'number of dancers'],
            songLength: ['song length', 'song_length', 'length']
          };

          // Map common column variations to standardized field names
          const findColumnIndex = (possibleNames) => {
            for (const name of possibleNames) {
              const index = headers.findIndex(h => h.includes(name.toLowerCase()));
              if (index !== -1) return index;
            }
            return -1;
          };

          const columnMap = {};
          const missingRequiredColumns = [];
          
          // Check for required columns
          Object.keys(requiredColumns).forEach(key => {
            const index = findColumnIndex(requiredColumns[key]);
            if (index >= 0) {
              columnMap[key] = index;
            } else if (['routineNumber', 'routineName'].includes(key)) {
              // These are absolutely required (performerName can be blank)
              missingRequiredColumns.push(requiredColumns[key][0]);
            }
          });

          if (missingRequiredColumns.length > 0) {
            console.log('Missing required columns:', missingRequiredColumns);
            const errorMessage = `Missing required columns: ${missingRequiredColumns.join(', ')}. Please ensure your CSV matches the template format.`;
            setCsvError(errorMessage);
            setModalNotification({ 
              type: 'error', 
              message: errorMessage 
            });
            return;
          }

          // Parse data rows with validation
          const dataRows = rows.slice(1).filter(row => row.trim());
          const routines = [];
          const validationErrors = [];
          
          dataRows.forEach((row, index) => {
            const values = row.split(",").map((v) => v.trim());
            
            // Validate routine data
            const routineNumber = columnMap.routineNumber >= 0 ? values[columnMap.routineNumber] : null;
            const routineName = columnMap.routineName >= 0 ? values[columnMap.routineName] : null;
            const performerName = columnMap.performerName >= 0 ? values[columnMap.performerName] : null;
            
            // Check for required fields
            if (!routineNumber || routineNumber === '') {
              validationErrors.push(`Row ${index + 2}: Missing routine number`);
            }
            if (!routineName || routineName === '') {
              validationErrors.push(`Row ${index + 2}: Missing routine name`);
            }
            // Note: performerName can be blank for group routines

            // Validate routine number format (alphanumeric with common separators allowed)
            if (routineNumber && !/^[a-zA-Z0-9\-_.]+$/.test(routineNumber.toString().trim())) {
              validationErrors.push(`Row ${index + 2}: Routine number contains invalid characters (letters, numbers, hyphens, underscores, and periods allowed)`);
            }

            if (validationErrors.length < 10) { // Limit error messages to avoid overwhelming UI
              const routine = {
                routineNumber: routineNumber,
                name: routineName,
                performerName: performerName,
                studioCode: columnMap.studioCode >= 0 ? values[columnMap.studioCode] : "",
                category: columnMap.category >= 0 ? values[columnMap.category] : "",
                division: columnMap.division >= 0 ? values[columnMap.division] : "",
                type: columnMap.type >= 0 ? values[columnMap.type] : "",
                ageGroup: columnMap.ageGroup >= 0 ? values[columnMap.ageGroup] : "",
                dancersCount: columnMap.dancersCount >= 0 ? values[columnMap.dancersCount] : "1",
                songLength: columnMap.songLength >= 0 ? values[columnMap.songLength] : null,
                isValid: !routineNumber || !routineName ? false : true
              };
              routines.push(routine);
            }
          });

          if (validationErrors.length > 0) {
            console.log('Validation errors found:', validationErrors);
            const errorMessage = `CSV validation failed:\n${validationErrors.slice(0, 5).join('\n')}${validationErrors.length > 5 ? `\n... and ${validationErrors.length - 5} more errors` : ''}`;
            setCsvError(errorMessage);
            setParsedRoutines(routines); // Show partial data for review
            setShowRoutinePreview(true); // Show preview even with errors
            setModalNotification({ 
              type: 'error', 
              message: `CSV validation failed with ${validationErrors.length} error(s). Check the preview for details.` 
            });
            return;
          }

          if (routines.length > 0) {
            setParsedRoutines(routines);
            setCsvError(null);
            setShowRoutinePreview(true);
            
            // Show validation success message
            console.log('Setting success notification');
            setModalNotification({ 
              type: 'success', 
              message: `CSV validated successfully! ${routines.length} routines ready for upload to ${stage.name}` 
            });
            
          } else {
            setCsvError('No valid routines found in CSV file');
          }
        } catch (err) {
          setCsvError('Error parsing CSV file. Please check the format and ensure it matches the template.');
          setModalNotification({ 
            type: 'error', 
            message: 'Error parsing CSV file. Please check the format and ensure it matches the template.' 
          });
          console.error("Error parsing CSV:", err);
        }
      };
      reader.readAsText(file);
      } else {
        console.log('Invalid file type:', file.type, file.name);
        const errorMsg = `Please select a valid CSV file. File type: ${file.type}`;
        setCsvError(errorMsg);
        setModalNotification({ 
          type: 'error', 
          message: errorMsg 
        });
        setCsvFile(null);
        setParsedRoutines([]);
      }
    } else {
      console.log('No file selected');
      const errorMsg = 'Please select a file';
      setCsvError(errorMsg);
      setModalNotification({ 
        type: 'error', 
        message: errorMsg 
      });
      setCsvFile(null);
      setParsedRoutines([]);
    }
  };

  // Validate CSV structure and format
  const validateCSVStructure = (rows) => {
    if (rows.length < 2) {
      return { isValid: false, error: 'CSV must have at least 2 rows (header + data)' };
    }

    const headerRow = rows[0];
    const headers = headerRow.split(',').map(h => h.trim());
    
    // Check if we have enough columns (minimum 3: routine #, name, performer)
    if (headers.length < 3) {
      return { isValid: false, error: 'CSV must have at least 3 columns. Please use the template format.' };
    }

    // Check if all data rows have the same number of columns as header
    const headerColumnCount = headers.length;
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      const columns = row.split(',');
      if (columns.length !== headerColumnCount) {
        return { 
          isValid: false, 
          error: `Row ${i + 1} has ${columns.length} columns, but header has ${headerColumnCount}. Please check your CSV format.` 
        };
      }
    }

    return { isValid: true };
  };

  // Confirm and upload validated routines
  const handleConfirmUpload = async () => {
    if (!selectedStage || !parsedRoutines.length) {
      setModalNotification({ type: 'error', message: 'Please select a valid CSV file first' });
      return;
    }

    try {
      setUploadingStageId(selectedStage.id);
      
      // Save schedule to backend
      await api.createStageSchedule(selectedEvent.name, selectedStage.id, parsedRoutines);
      
      // Update local state
      const updatedEventStages = { ...eventStages };
      const stageIndex = updatedEventStages[selectedEvent.name].stages.findIndex(s => s.id === selectedStage.id);
      
      if (stageIndex !== -1) {
        updatedEventStages[selectedEvent.name].stages[stageIndex] = {
          ...selectedStage,
          schedule: parsedRoutines,
          routineCount: parsedRoutines.length,
          uploadDate: new Date().toISOString()
        };
        
        setEventStages(updatedEventStages);
      }

      // Show success message in modal
      setModalNotification({ 
        type: 'success', 
        message: `Successfully uploaded ${parsedRoutines.length} routines to ${selectedStage.name}` 
      });

      // Clear the preview
      setCsvFile(null);
      setCsvError(null);
      setParsedRoutines([]);
      setSelectedStage(null);
      setShowRoutinePreview(false);
      
    } catch (err) {
      console.error("Error saving schedule:", err);
      setModalNotification({ 
        type: 'error', 
        message: `Failed to save schedule: ${err.message || 'Unknown error'}` 
      });
    } finally {
      setUploadingStageId(null);
    }
  };







  // Remove schedule from stage
  const handleRemoveSchedule = async (stage) => {
    try {
      // Delete from backend
      await api.deleteStageSchedule(selectedEvent.name, stage.id);
      
      // Update local state
      const updatedEventStages = { ...eventStages };
      const stageIndex = updatedEventStages[selectedEvent.name].stages.findIndex(s => s.id === stage.id);
      
      if (stageIndex !== -1) {
        updatedEventStages[selectedEvent.name].stages[stageIndex] = {
          ...stage,
          schedule: null,
          routineCount: 0,
          uploadDate: null
        };
        
        setEventStages(updatedEventStages);
        
        // Show page-level success after a short delay
        setTimeout(() => {
          setNotification({ type: 'success', message: 'Schedule removed successfully' });
        }, 100);
      }
    } catch (err) {
      console.error("Error removing schedule:", err);
      setNotification({ 
        type: 'error', 
        message: `Failed to remove schedule: ${err.message || 'Unknown error'}` 
      });
    }
  };

  // Handle viewing existing schedule
  const handleViewSchedule = async (stage) => {
    try {
      setSelectedStage(stage);
      
      if (stage.schedule && stage.schedule.length > 0) {
        // Use cached schedule data (legacy CSV format)
        setParsedRoutines(stage.schedule);
        setCsvError(null);
        setRoutineSearchQuery('');
        setShowRoutinePreview(true);
      } else if (stage.routineCount > 0) {
        // Load schedule from backend
        const scheduleData = await api.getStageSchedule(selectedEvent.name, stage.id);
        if (scheduleData && scheduleData.routines) {
          // Check if this is enhanced JSON data or legacy CSV data
          const isEnhancedData = scheduleData.enhanced || 
                                 scheduleData.routines.some(r => r.parentRoutine || r.stage || r.cluster);
          
          if (isEnhancedData) {
            // Use enhanced viewer for rich JSON data
            setEnhancedRoutineData(scheduleData.routines);
            setShowEnhancedViewer(true);
          } else {
            // Use legacy viewer for CSV data
            setParsedRoutines(scheduleData.routines);
            setCsvError(null);
            setRoutineSearchQuery('');
            setShowRoutinePreview(true);
          }
          
          // Update local cache
          const updatedEventStages = { ...eventStages };
          const stageIndex = updatedEventStages[selectedEvent.name].stages.findIndex(s => s.id === stage.id);
          if (stageIndex !== -1) {
            updatedEventStages[selectedEvent.name].stages[stageIndex].schedule = scheduleData.routines;
            setEventStages(updatedEventStages);
          }
        }
      }
    } catch (err) {
      console.error("Error loading schedule:", err);
      setNotification({ 
        type: 'error', 
        message: `Failed to load schedule: ${err.message || 'Unknown error'}` 
      });
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowStageModal(false);
    setSelectedEvent(null);
    setSelectedStage(null);
    setCsvFile(null);
    setCsvError(null);
    setParsedRoutines([]);
    setShowRoutinePreview(false);
    setShowEnhancedViewer(false);
    setEnhancedRoutineData([]);
    setCurrentStep(0);
    setModalNotification(null);
  };

  // Handle JSON file upload
  const handleJSONUpload = async () => {
    if (!jsonFile || !jsonEventName.trim()) {
      setNotification({ 
        type: 'error', 
        message: 'Please select a JSON file and enter an event name' 
      });
      return;
    }

    try {
      setJsonUploading(true);
      
      // Debug: Check if the function exists
      console.log('API object:', api);
      console.log('uploadCompetitionScheduleJSON function:', api.uploadCompetitionScheduleJSON);
      
      if (!api.uploadCompetitionScheduleJSON) {
        // Fallback: Try direct API call
        console.log('Function not found in api object, trying direct API call');
        
        // Read the JSON file
        const fileContent = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsText(jsonFile);
        });

        const scheduleData = JSON.parse(fileContent);
        
        const STAGES_API = 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/stages';
        const result = await fetchWithAuth(`${STAGES_API}/upload-json`, {
          method: "POST",
          body: JSON.stringify({ scheduleData, eventName: jsonEventName }),
        });
        
        setNotification({ 
          type: 'success', 
          message: `Successfully processed ${result.routinesProcessed || 'unknown'} routines across ${result.stagesCreated || 'unknown'} stages!` 
        });
        
        // Reset upload state
        setShowJSONUpload(false);
        setJsonFile(null);
        setJsonEventName('');
        
        // Refresh data
        await fetchEvents();
        await fetchStageConfigurations();
        return;
      }
      
      // Read the JSON file
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(jsonFile);
      });

      const scheduleData = JSON.parse(fileContent);
      
      // Upload to API
      const result = await api.uploadCompetitionScheduleJSON(scheduleData, jsonEventName);
      
      setNotification({ 
        type: 'success', 
        message: `Successfully processed ${result.routinesProcessed} routines across ${result.stagesCreated} stages!` 
      });
      
      // Reset upload state
      setShowJSONUpload(false);
      setJsonFile(null);
      setJsonEventName('');
      
      // Refresh data
      await fetchEvents();
      await fetchStageConfigurations();
      
    } catch (err) {
      console.error("Error uploading JSON:", err);
      setNotification({ 
        type: 'error', 
        message: `Failed to upload JSON: ${err.message || 'Unknown error'}` 
      });
    } finally {
      setJsonUploading(false);
    }
  };

  // Download sample CSV template
  const handleDownloadTemplate = () => {
    const csvContent = [
      'Routine #,Routine Name,Performer Name,Studio Code,Category,Division,Type,Age Group,# of Dancers,Song Length',
      '1,Example Routine,John Doe,ABC,Solo,Competitive,Jazz,Teen,1,2:30',
      '2,Another Routine,Jane Smith,XYZ,Duet,Recreational,Hip Hop,Junior,2,3:00'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'routine_schedule_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Modal step configuration
  const getStepConfig = () => {
    if (!selectedEvent) return null;
    
    const hasStages = eventStages[selectedEvent.name];
    
    if (!hasStages) {
      return [
        { key: 'configure', title: 'Configure Stages', icon: Settings },
        { key: 'overview', title: 'Manage Stages', icon: Theater }
      ];
    } else {
      return [
        { key: 'overview', title: 'Manage Stages', icon: Theater },
        { key: 'upload', title: 'Upload Schedule', icon: Upload }
      ];
    }
  };

  const filteredEvents = getFilteredAndSortedEvents();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Theater className="h-8 w-8 text-purple-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Stages Manager
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Configure stages and upload routine schedules for your events
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowJSONUpload(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Upload className="h-4 w-4" />
                <span>Upload JSON</span>
              </button>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>Download Template</span>
              </button>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
            notification.type === 'error' 
              ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400' 
              : 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400'
          }`}>
            {notification.type === 'error' ? (
              <AlertCircle className="h-5 w-5" />
            ) : (
              <CheckCircle className="h-5 w-5" />
            )}
            <span>{notification.message}</span>
            <button 
              onClick={() => setNotification(null)}
              className="ml-auto text-current hover:opacity-70"
            >
              ×
            </button>
          </div>
        )}

        {/* Filters and Search */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-10 pr-4 py-2 w-full rounded-lg border ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
              />
            </div>
          </div>
          
          <div className="sm:w-64">
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
            >
              <option value="all">All Seasons</option>
              {seasons.map((season) => (
                <option key={season.name} value={season.name}>
                  {season.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Events Table */}
        <div className={`rounded-lg border overflow-hidden ${
          isDarkMode
            ? "bg-card-gradient backdrop-blur-sm border-brand-border-logo shadow-lg"
            : "bg-white border-gray-200 shadow-sm"
        }`}>
          {filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <Theater className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Events Found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery 
                  ? `No events match "${searchQuery}"`
                  : selectedSeason === 'all' 
                  ? 'No events have been created yet. Create events in Season & Event Settings.'
                  : `No events found for ${selectedSeason} season.`
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} border-b`}>
                  <tr>
                    <th 
                      onClick={() => handleSort('name')}
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer group ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        Event Name
                        <ArrowUpDown className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                          sortConfig.key === 'name' ? 'opacity-100' : ''
                        }`} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('city')}
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer group ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        Location
                        <ArrowUpDown className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                          sortConfig.key === 'city' ? 'opacity-100' : ''
                        }`} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('startDate')}
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer group ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        Dates
                        <ArrowUpDown className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                          sortConfig.key === 'startDate' ? 'opacity-100' : ''
                        }`} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('season')}
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer group ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        Season
                        <ArrowUpDown className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                          sortConfig.key === 'season' ? 'opacity-100' : ''
                        }`} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('stages')}
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer group ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        Stages
                        <ArrowUpDown className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                          sortConfig.key === 'stages' ? 'opacity-100' : ''
                        }`} />
                      </div>
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Routines
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
                  {filteredEvents.map((event) => {
                    const hasStages = eventStages[event.name];
                    const totalRoutines = hasStages ? hasStages.stages.reduce((total, stage) => total + stage.routineCount, 0) : 0;
                    
                    return (
                      <tr 
                        key={event.name} 
                        className={`${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} transition-colors`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {event.name}
                            </div>
                            {event.eventType && (
                              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {event.eventType}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                              {event.city || 'Enhanced Event'}
                              {event.synthetic && (
                                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                  isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                                }`}>
                                  JSON
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <CalendarDays className="h-4 w-4 text-gray-400 mr-2" />
                            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                              {formatDate(event.startDate)} - {formatDate(event.endDate)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 text-gray-400 mr-2" />
                            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                              {event.season}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {hasStages ? (
                            <div className="flex items-center">
                              <Theater className="h-4 w-4 text-green-500 mr-2" />
                              <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                {hasStages.stageCount} Stage{hasStages.stageCount > 1 ? 's' : ''}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <Settings className="h-4 w-4 text-yellow-500 mr-2" />
                              <span className={`text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                Not configured
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {hasStages && hasStages.stages ? (
                            <div className="flex items-center gap-1">
                              {hasStages.stages.map((stage, index) => {
                                const stageInitial = stage.name.charAt(0).toUpperCase();
                                const routineCount = stage.routineCount || 0;
                                return (
                                  <div 
                                    key={stage.id || index}
                                    className={`flex items-center justify-center w-8 h-6 rounded text-xs font-bold ${
                                      routineCount > 0
                                        ? isDarkMode 
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-blue-500 text-white'
                                        : isDarkMode
                                          ? 'bg-gray-700 text-gray-400'
                                          : 'bg-gray-300 text-gray-600'
                                    }`}
                                    title={`${stage.name}: ${routineCount} routines`}
                                  >
                                    {routineCount > 0 ? routineCount : stageInitial}
                                  </div>
                                );
                              })}
                              <div className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {totalRoutines} total
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <Settings className="h-4 w-4 text-gray-400 mr-2" />
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                No stages configured
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEventSelect(event)}
                              className={`p-2 rounded-lg transition-colors ${
                                isDarkMode 
                                  ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                              }`}
                              title={hasStages ? 'Manage stages' : 'Configure stages'}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            
                            {hasStages && totalRoutines > 0 && (
                              <button
                                onClick={() => handleOpenScheduleCreator(event)}
                                className={`p-2 rounded-lg transition-colors ${
                                  isDarkMode 
                                    ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-900/20' 
                                    : 'text-purple-600 hover:text-purple-700 hover:bg-purple-100'
                                }`}
                                title="Create Intelligent Schedule"
                              >
                                <Calendar className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Stage Management Modal */}
      <StageManagementModal
        isOpen={showStageModal}
        onClose={handleCloseModal}
        selectedEvent={selectedEvent}
        isDarkMode={isDarkMode}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        stageCount={stageCount}
        setStageCount={setStageCount}
        stageNames={stageNames}
        setStageNames={setStageNames}
        csvFile={csvFile}
        setCsvFile={setCsvFile}
        csvError={csvError}
        setCsvError={setCsvError}
        parsedRoutines={parsedRoutines}
        setParsedRoutines={setParsedRoutines}
        uploadingStageId={uploadingStageId}
        eventStages={eventStages}
        selectedStage={selectedStage}
        modalNotification={modalNotification}
        setModalNotification={setModalNotification}
        onConfigureStages={handleConfigureStages}
        onStageCountChange={handleStageCountChange}
        onStageNameChange={handleStageNameChange}
        onFileSelect={handleFileSelect}
        onViewSchedule={handleViewSchedule}
        onRemoveSchedule={handleRemoveSchedule}
      />

      {/* Routine Preview Modal */}
      {showRoutinePreview && selectedStage && parsedRoutines.length > 0 && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm" 
            onClick={() => setShowRoutinePreview(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div 
              className={`relative w-full max-w-6xl rounded-lg shadow-xl ${
                isDarkMode 
                  ? 'bg-[#1a1f2d]' 
                  : 'bg-white'
              }`}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <div>
                  <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Routine Schedule Preview
                  </h3>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {selectedStage.name} • {
                      routineSearchQuery ? 
                        `${parsedRoutines.filter(routine => {
                          const query = routineSearchQuery.toLowerCase();
                          return (
                            routine.routineNumber?.toString().toLowerCase().includes(query) ||
                            routine.name?.toLowerCase().includes(query) ||
                            routine.performerName?.toLowerCase().includes(query) ||
                            routine.studioCode?.toLowerCase().includes(query) ||
                            routine.category?.toLowerCase().includes(query) ||
                            routine.division?.toLowerCase().includes(query) ||
                            routine.ageGroup?.toLowerCase().includes(query)
                          );
                        }).length} of ${parsedRoutines.length} routines` :
                        `${parsedRoutines.length} routines`
                    }
                  </p>
                </div>
                <button 
                  onClick={() => {
                    setShowRoutinePreview(false);
                    setRoutineSearchQuery('');
                  }} 
                  className="btn btn-ghost btn-sm btn-circle"
                  type="button"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
                {csvError ? (
                  <div className={`p-4 rounded-lg border mb-4 ${
                    isDarkMode 
                      ? 'bg-red-900/20 border-red-800 text-red-400' 
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold mb-1">Validation Issues</h4>
                        <pre className="text-sm whitespace-pre-wrap">{csvError}</pre>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`p-4 rounded-lg border mb-4 ${
                    isDarkMode 
                      ? 'bg-green-900/20 border-green-800 text-green-400' 
                      : 'bg-green-50 border-green-200 text-green-700'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5" />
                      <div>
                        <h4 className="font-semibold">CSV Validation Passed</h4>
                        <p className="text-sm">All routines are properly formatted and ready for upload.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search routines..."
                      value={routineSearchQuery}
                      onChange={(e) => setRoutineSearchQuery(e.target.value)}
                      className={`w-full px-4 py-3 pl-10 rounded-lg border ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Routine Table */}
                <div className={`rounded-lg border overflow-hidden ${
                  isDarkMode ? 'border-gray-600' : 'border-gray-200'
                }`}>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <tr>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            #
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            Routine Name
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            Performer
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            Studio
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            Category
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            Division
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            Age Group
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            # of Dancers
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            Song Length
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`${isDarkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                        {parsedRoutines
                          .filter(routine => {
                            if (!routineSearchQuery) return true;
                            const query = routineSearchQuery.toLowerCase();
                            return (
                              routine.routineNumber?.toString().toLowerCase().includes(query) ||
                              routine.name?.toLowerCase().includes(query) ||
                              routine.performerName?.toLowerCase().includes(query) ||
                              routine.studioCode?.toLowerCase().includes(query) ||
                              routine.category?.toLowerCase().includes(query) ||
                              routine.division?.toLowerCase().includes(query) ||
                              routine.ageGroup?.toLowerCase().includes(query)
                            );
                          })
                          .map((routine, index) => (
                          <tr key={index} className={`${
                            routine.isValid === false 
                              ? isDarkMode ? 'bg-red-900/20' : 'bg-red-50' 
                              : ''
                          }`}>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              {routine.routineNumber}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              {routine.name}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              {routine.performerName || (
                                <span className={`italic ${
                                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                  Group/Multiple
                                </span>
                              )}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              {routine.studioCode}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              {routine.category}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              {routine.division}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              {routine.ageGroup}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              {routine.dancersCount || (
                                <span className={`italic ${
                                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                  Not specified
                                </span>
                              )}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              {routine.songLength || (
                                <span className={`italic ${
                                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                  Not specified
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {routine.isValid === false ? (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-800'
                                }`}>
                                  Invalid
                                </span>
                              ) : (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-800'
                                }`}>
                                  Valid
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className={`flex justify-end gap-3 p-6 border-t ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <button
                  onClick={() => {
                    setShowRoutinePreview(false);
                    setRoutineSearchQuery('');
                  }}
                  className={`px-6 py-2 rounded-lg border font-medium transition-colors ${
                    isDarkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpload}
                  disabled={uploadingStageId || csvError}
                  className="px-8 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {uploadingStageId ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Confirm Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Creator Modal */}
      {showScheduleCreator && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full h-full max-w-7xl max-h-[90vh] rounded-xl ${
            isDarkMode ? 'bg-gray-900' : 'bg-white'
          } overflow-hidden flex flex-col`}>
            {/* Header */}
            <div className={`flex items-center justify-between p-6 border-b ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Schedule Creator - {scheduleCreatorData.eventName}
              </h2>
              <button
                onClick={() => setShowScheduleCreator(false)}
                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Schedule Creator Content */}
            <div className="flex-1 overflow-hidden">
              <ScheduleCreator
                routines={scheduleCreatorData.routines}
                stages={scheduleCreatorData.stages}
                eventName={scheduleCreatorData.eventName}
                onScheduleUpdate={(schedule) => {
                  console.log('Schedule updated:', schedule);
                  // TODO: Save schedule back to stages
                }}
                onExport={(format, data) => {
                  console.log('Export requested:', format, data);
                  // TODO: Handle export functionality
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Routine Viewer */}
      <EnhancedRoutineViewer
        routines={enhancedRoutineData}
        stageName={selectedStage?.name || 'Stage'}
        onClose={() => {
          setShowEnhancedViewer(false);
          setEnhancedRoutineData([]);
        }}
        show={showEnhancedViewer}
      />

      {/* JSON Upload Modal */}
      {showJSONUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-xl ${
            isDarkMode ? 'bg-gray-900' : 'bg-white'
          } overflow-hidden`}>
            {/* Header */}
            <div className={`flex items-center justify-between p-6 border-b ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Upload Competition Schedule JSON
              </h2>
              <button
                onClick={() => setShowJSONUpload(false)}
                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Event Name
                </label>
                <input
                  type="text"
                  value={jsonEventName}
                  onChange={(e) => setJsonEventName(e.target.value)}
                  placeholder="Enter event name (e.g., Los Angeles Championship)"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Competition Schedule JSON File
                </label>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setJsonFile(e.target.files[0])}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <p className={`text-xs mt-1 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Upload your competition_schedule.json file to automatically configure all stages and routines
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div className={`flex items-center justify-end space-x-3 p-6 border-t ${
              isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
            }`}>
              <button
                onClick={() => setShowJSONUpload(false)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isDarkMode 
                    ? 'text-gray-300 hover:bg-gray-700' 
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleJSONUpload}
                disabled={jsonUploading || !jsonFile || !jsonEventName.trim()}
                className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                  jsonUploading || !jsonFile || !jsonEventName.trim()
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {jsonUploading && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>{jsonUploading ? 'Processing...' : 'Upload & Process'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StagesManager;